import {
	streamsSourceId,
	liveTrafficSourceId,
	alwaysClickableLayers,
	operatingPictureFillLayerId,
	operatingPictureSymbolLayerId
} from 'constants/mapConstants';
import { orderBy, uniqBy, startCase } from 'lodash';
import { getLiveTrafficTableColumns } from 'components/airSpace/airspaceUtils';
import { getOperatingPictureColumns } from 'components/airSpace/airspaceConstants';
import destination from '@turf/destination';
import { convertDegreesToThreeDigit } from 'components/airSpace/airspaceUtils';
import distance from '@turf/distance';
import { t } from 'i18next';
import dayjs from 'dayjs';
import { browserTimeZone, dateDisplayTimeMinutesSeconds } from 'utils/timeHelpers';
import {
	getAMAircraftType,
	getAMAltitudeDisplay,
	getAMGroundSpeedDisplay,
	convertDegreesToCardinalDirection
} from 'components/airSpace/airspaceUtils';
import VideocamIcon from '@mui/icons-material/Videocam';
import { $danger } from 'constants/styles';
import { convertUTCStringToLocalString } from 'utils/timeHelpers';

export const formatFeatureForOpsView = f => {
	return {
		id: f.properties.id,
		layerId: f.layer.id,
		source: f.source
	};
};

export const getFeatureCurrentInfo = (
	simplifiedFeature,
	trafficFeaturesFormattedForMap,
	streams
) => {
	const { id, source } = simplifiedFeature;
	const isStream = source === streamsSourceId;
	const isLiveTraffic = source === liveTrafficSourceId;

	if (isLiveTraffic) {
		return {
			...simplifiedFeature,
			...trafficFeaturesFormattedForMap.find(f => f.id === id)
		};
	}

	if (isStream) {
		return {
			...simplifiedFeature,
			...streams.find(s => s.id === id)
		};
	}

	return null;
};

export const opsViewDetailsUrl = 'ops-details';

export const isPerson = f => {
	return f.properties.object_type === 'userOnly' || f.properties.object_type === 'userStream';
};

export const isAircraft = f => {
	return (
		f.properties.object_type === 'aircraftStream' || f.properties.object_type === 'aircraftOnly'
	);
};

export const isStream = f => {
	return f.properties.object_type === 'streamOnly';
};

export const getMapFeaturesToShowOpsView = ({
	newClickedFeatures,
	visibleMapLayers,
	zoom,
	units
}) => {
	const layersWithinZoom = visibleMapLayers?.filter(layer => {
		return layer?.preview_properties?.zoom_min < zoom && layer?.preview_properties?.zoom_max > zoom;
	});

	const newClickedFeaturesToShow = newClickedFeatures?.filter(feature => {
		const simplifiedFeatureLayerId = feature.layer.id.split('-style-')[0];
		const isAlwaysClickable = alwaysClickableLayers.includes(simplifiedFeatureLayerId);
		const isInLayersWithinZoom = layersWithinZoom.find(
			layer => layer.id === simplifiedFeatureLayerId
		);

		return isAlwaysClickable || isInLayersWithinZoom;
	});

	//Create a hash based on the features's properties object and then find unique features based on that hash
	const hashFeature = feature => {
		const properties = feature.properties;
		return JSON.stringify(properties);
	};

	const uniqueFeatures = uniqBy(newClickedFeaturesToShow, hashFeature);

	const formattedFeaturesToShow = uniqueFeatures.map(feature => {
		const simplifiedFeatureLayerId = feature.layer.id.split('-style-')[0];
		const layersToUse = [...layersWithinZoom, ...alwaysClickableLayers];
		const parentLayer = layersToUse.find(layer => layer.id === simplifiedFeatureLayerId);
		let preview_properties = [];
		if (parentLayer?.preview_properties?.properties) {
			preview_properties = parentLayer.preview_properties.properties;
		} else {
			if (isAircraft(feature)) {
				preview_properties = getLiveTrafficTableColumns({ isForPreviewProp: true, units });
			}
			if (
				simplifiedFeatureLayerId === operatingPictureFillLayerId ||
				simplifiedFeatureLayerId === operatingPictureSymbolLayerId
			) {
				preview_properties = getOperatingPictureColumns(true);
			}
		}

		return {
			...feature,
			preview_properties
		};
	});

	return formattedFeaturesToShow;
};

export const createOpsViewTrackingInfoFeaturesSource = features => {
	//Take the feature's coordinates and add 1 mile along the heading
	const headingFeatures = features.map(feature => {
		const properties = feature?.properties;
		const coordinates = feature?.geometry?.coordinates;
		const heading = properties?.heading;
		if (!coordinates || !heading) {
			return null;
		}
		const point = destination(coordinates, 0.025, heading, { units: 'miles' });
		return {
			...point,
			properties: {
				...properties,
				label_type: 'heading',
				heading: convertDegreesToThreeDigit(heading)
			}
		};
	});

	//Create AGL features that are 180 degrees from the heading features
	const aglFeatures = features.map(feature => {
		const properties = feature?.properties;
		const coordinates = feature?.geometry?.coordinates;
		const heading = properties?.heading;
		if (!coordinates || !heading) {
			return null;
		}
		const point = destination(coordinates, 0.025, heading + 180, { units: 'miles' });
		return {
			...point,
			properties: {
				...properties,
				label_type: 'agl'
			}
		};
	});

	return {
		type: 'geojson',
		data: {
			type: 'FeatureCollection',
			features: [...headingFeatures, ...aglFeatures]
		}
	};
};

export const createOpsViewTrackingCircleAndHeadingLineFeaturesSource = features => {
	// const radiusFeatures = features.map(feature => {
	// 	const coordinates = feature?.geometry?.coordinates;
	// 	if (!coordinates) {
	// 		return null;
	// 	}
	// 	const circleFeature = circle(coordinates, 0.05, { units: 'miles' });
	// 	return {
	// 		...circleFeature,
	// 		properties: feature.properties
	// 	};
	// });

	// const headingLineFeatures = features.map(feature => {
	// 	const coordinates = feature?.geometry?.coordinates;
	// 	const heading = feature?.properties?.heading;
	// 	if (!coordinates || !heading) {
	// 		return null;
	// 	}
	// 	const line = createLineAlongHeading(coordinates, heading, 0.05);
	// 	return {
	// 		...line,
	// 		properties: feature.properties
	// 	};
	// });

	return {
		type: 'geojson',
		data: {
			type: 'FeatureCollection',
			features: [
				// ...headingLineFeatures,
				// ...radiusFeatures,
				...features
			] //include features so that there is a center point to display text
		}
	};
};

export const formatTrafficDataForOpsViewTable = (data, mapPinLocation, units, hideFromPreview) => {
	const dataWithDistanceFromPin = data.map(index => {
		return {
			...index,
			distanceFromPin: distance(index?.geometry?.coordinates, mapPinLocation.geometry.coordinates)
		};
	});

	const orderedData = orderBy(dataWithDistanceFromPin, ['distanceFromPin'], ['asc']);

	const columns = getOpsViewTrafficTableColumns({ units });

	return {
		columns,
		rows: orderedData
	};
};

export const getOpsViewTrafficTableColumns = ({ isForPreviewProp, units }) => {
	const airMeshColumns = [
		{
			field: 'timestamp',
			headerName: t('Timestamp'),
			width: 225,
			renderCell: params => {
				return dayjs
					.unix(params.row.timestamp / 1000) //Receiving in microseconds
					.tz(browserTimeZone)
					.format(dateDisplayTimeMinutesSeconds);
			}
		},
		{ field: 'provider_id', headerName: t('Provider ID'), width: 150 },
		{
			field: 'aircraft_type',
			headerName: t('Type'),
			width: 100,
			renderCell: parameters => {
				return getAMAircraftType(parameters.row.aircraft_type)?.label;
			}
		},
		{
			field: 'stream',
			headerName: t('Video Feed'),
			width: 150,
			renderCell: params => (params.row.stream?.id ? <VideocamIcon sx={{ color: $danger }} /> : ''),
			type: 'boolean'
		},
		{
			field: 'altitude_agl_ft',
			headerName: units === 'imperial' ? t('Altitude AGL (ft)') : t('Altitude AGL (m)'),
			width: 175,
			renderCell: parameters =>
				getAMAltitudeDisplay(parameters.row, parameters.row.altitude_agl_ft, false)
		},
		{
			field: 'heading',
			headerName: t('Heading'),
			width: 150,
			renderCell: params => {
				return convertDegreesToCardinalDirection(params.row.heading);
			}
		},
		{
			field: 'ground_speed',
			headerName: t('Ground Speed'),
			width: 175,
			renderCell: parameters => getAMGroundSpeedDisplay({ row: parameters.row })
		},
		{ field: 'lat', headerName: t('Latitude'), width: 150 },
		{ field: 'long', headerName: t('Longitude'), width: 150 },
		{
			field: 'altitude_rel_ft',
			headerName: units === 'imperial' ? t('Altitude REL (ft)') : t('Altitude REL (m)'),
			width: 175,
			renderCell: parameters =>
				getAMAltitudeDisplay(parameters.row, parameters.row.altitude_rel_ft, false)
		},
		{
			field: 'altitude_msl_ft',
			headerName: units === 'imperial' ? t('Altitude MSL (ft)') : t('Altitude MSL (m)'),
			width: 175,
			renderCell: parameters =>
				getAMAltitudeDisplay(parameters.row, parameters.row.altitude_msl_ft, false)
		},
		{
			field: 'emergency_state',
			headerName: t('Emergency State'),
			width: 200,
			renderCell: parameters => {
				return parameters.row.emergency_state ? t('None') : t('N/A');
			}
		},
		{
			field: 'signal_source',
			headerName: t('Signal Source'),
			width: 150,
			renderCell: parameters => {
				return parameters.row.signal_source ? parameters.row.signal_source : t('N/A');
			}
		},
		{
			field: 'id',
			width: 350,
			headerName: t('Track ID')
		},
		{
			field: 'provider',
			headerName: t('Provider'),
			width: 200,
			renderCell: parameters => {
				return startCase(parameters.row.provider);
			}
		},
		{
			field: 'is_sim',
			headerName: t('Simulated'),
			width: 100,
			renderCell: parameters => {
				return parameters.row.is_sim ? t('Yes') : t('No');
			}
		},
		{
			field: 'rid',
			headerName: t('Operator ID'),
			width: 200
		},
		{
			field: 'sn',
			headerName: t('Serial Number'),
			width: 200
		},
		{
			field: 'operator_lat',
			headerName: t('Takeoff Latitude'),
			width: 150
		},
		{
			field: 'operator_long',
			headerName: t('Takeoff Longitude'),
			width: 150
		}
	];
	return isForPreviewProp ? airMeshColumns.filter(col => !col.hideFromPreview) : airMeshColumns;
};

export const getOpsViewUsersTableColumns = () => {
	return [
		{
			field: 'firstname',
			headerName: t('First Name'),
			width: 150,
			renderCell: params => params.row?.properties?.firstname || ''
		},
		{
			field: 'lastname',
			headerName: t('Last Name'),
			width: 150,
			renderCell: params => params.row?.properties?.lastname || ''
		},
		{
			field: 'last_location_recorded_at',
			headerName: t('Last Ping'),
			width: 200,
			renderCell: params => {
				return convertUTCStringToLocalString(params.row.last_location_recorded_at);
			}
		},
		{
			field: 'stream',
			headerName: t('Video Feed'),
			width: 150,
			renderCell: params =>
				params.row?.stream?.id ? <VideocamIcon sx={{ color: $danger }} /> : '',
			type: 'boolean'
		},
		{
			field: 'email',
			headerName: t('Email'),
			width: 200,
			renderCell: params => params.row?.properties?.email || ''
		},
		{
			field: 'lat',
			headerName: t('Latitude'),
			width: 150,
			renderCell: params => params.row?.geometry?.coordinates[1]
		},
		{
			field: 'long',
			headerName: t('Longitude'),
			width: 150,
			renderCell: params => params.row?.geometry?.coordinates[0]
		}
	];
};
